import { useMemo } from "react";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { ReferenceEntityName } from "../../../../api/types/field-type";
import { ReferenceFieldValue } from "../types";
import { useTagList } from "../../../../api/queries/tag/list";
import { useJourneyTypes } from "../../../../api/queries/enum/useJourneyTypes";
import { useCampusActivities } from "../../../../api/queries/enum/useCampusActivities";
import { useCountries } from "../../../../api/queries/enum/useCountries";

interface FrontendFilterStrategyProps {
  value: ReferenceFieldValue | null;
  onChange: (value: ReferenceFieldValue | null) => void;
  referenceEntity: ReferenceEntityName;
  component: "autocomplete" | "dropdown";
  placeholder: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

export const FrontendFilterStrategy: React.FC<FrontendFilterStrategyProps> = ({
  value,
  onChange,
  referenceEntity,
  component,
  placeholder,
  disabled = false,
  error = false,
  className = "",
}) => {
  // Load all data at once based on entity type
  const { data: tagsData, isLoading: isLoadingTags } = useTagList();
  const { data: journeyTypesData, isLoading: isLoadingJourneyTypes } =
    useJourneyTypes();
  const { data: campusActivitiesData, isLoading: isLoadingCampusActivities } =
    useCampusActivities();
  const { data: countriesData, isLoading: isLoadingCountries } = useCountries();

  const { suggestions, isLoading } = useMemo(() => {
    let items: any[] = [];
    let loading = false;

    if (referenceEntity === ReferenceEntityName.Tag) {
      loading = isLoadingTags;
      items = tagsData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.JourneyType) {
      loading = isLoadingJourneyTypes;
      items = journeyTypesData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.CampusActivity) {
      loading = isLoadingCampusActivities;
      items = campusActivitiesData?.data?.results || [];
    } else if (referenceEntity === ReferenceEntityName.Country) {
      loading = isLoadingCountries;
      items = countriesData?.data?.results || [];
    }

    return { suggestions: items, isLoading: loading };
  }, [
    referenceEntity,
    tagsData,
    journeyTypesData,
    campusActivitiesData,
    countriesData,
    isLoadingTags,
    isLoadingJourneyTypes,
    isLoadingCampusActivities,
    isLoadingCountries,
  ]);

  const handleChange = (
    event: AutoCompleteChangeEvent | DropdownChangeEvent,
  ) => {
    if (typeof event.value === "string") {
      // For AutoComplete, user typed something
      onChange(null);
    } else if (event.value) {
      // User selected something
      const item = event.value;
      onChange({
        id: item.id,
        label: item.name,
      });
    } else {
      // Cleared
      onChange(null);
    }
  };

  if (component === "dropdown") {
    // For dropdown, find the selected item from suggestions
    // Handle both object {id, label} and string ID formats
    const selectedItem = value
      ? suggestions.find(
          (item) => item.id === (typeof value === "string" ? value : value.id),
        ) || null
      : null;

    return (
      <Dropdown
        value={selectedItem}
        onChange={handleChange}
        options={suggestions}
        optionLabel="name"
        placeholder={placeholder}
        disabled={disabled || isLoading}
        className={`w-full ${error ? "p-invalid" : ""} ${className}`}
        filter
        showClear
        panelClassName="overlay-elevated"
      />
    );
  }

  // For AutoComplete, find the selected item from suggestions
  // Handle both object {id, label} and string ID formats
  const selectedItem = value
    ? suggestions.find(
        (item) => item.id === (typeof value === "string" ? value : value.id),
      ) || null
    : null;

  return (
    <AutoComplete
      value={selectedItem}
      onChange={handleChange}
      suggestions={suggestions}
      field="name"
      placeholder={placeholder}
      disabled={disabled || isLoading}
      className={`w-full ${error ? "p-invalid" : ""} ${className}`}
      completeMethod={() => {}}
      forceSelection
      dropdown
    />
  );
};
